import { useMutation, useQuery, useQueryClient } from "react-query";
import { useDispatch } from "react-redux";
import { v1 as uuidv1 } from "uuid";
import * as accountClient from "../account/client";
import { addMessage } from "../alerts/messagesSlice";
import { useUpdateCustomer } from "./hooks_cl";

const useUserPreferences = (userId) => {
    return useQuery(
        [`userPrefs`, userId],
        () => accountClient.getUserPreferences(userId),
        { staleTime: 60000 }
    );
};
const useUserSubscriptions = (userId) => {
    return useQuery(
        [`userSubs`, userId],
        () => accountClient.getUserSubscriptions(userId),
        { staleTime: 60000 }
    );
};

const useUpdatePassword = () => {
    const dispatch = useDispatch();
    return useMutation((values) => accountClient.updateUser(values), {
        onError: (err) => {
            dispatch(
                addMessage({
                    id: uuidv1(),
                    text: `Error updating password: ${err}`,
                    priority: `low`,
                })
            );
        },
        onSuccess: (data, variables) => {
            dispatch(
                addMessage({
                    id: uuidv1(),
                    text: `Password updated`,
                    priority: `low`,
                })
            );
        },
    });
};

const useUpdateUser = () => {
    const queryClient = useQueryClient();
    const mutateCustomer = useUpdateCustomer();
    const dispatch = useDispatch();
    return useMutation((values) => accountClient.updateUser(values), {
        onError: (err) => {
            dispatch(
                addMessage({
                    id: uuidv1(),
                    text: `Error updating user: ${err}`,
                    priority: `low`,
                })
            );
        },
        onSuccess: (data, variables) => {
            queryClient.invalidateQueries([`user`, data.id]).then();
            if (variables.updates.email && data.cl_customer_id) {
                mutateCustomer.mutate({
                    id: data.cl_customer_id,
                    updates: { email: variables.updates.email },
                });
            }
        },
    });
};

const useUpdateUserPreferences = () => {
    const queryClient = useQueryClient();
    const dispatch = useDispatch();
    return useMutation(
        (values) => accountClient.updateUserPreferences(values),
        {
            onError: (err) => {
                dispatch(
                    addMessage({
                        id: uuidv1(),
                        text: `Error updating user preferences: ${err}`,
                        priority: `low`,
                    })
                );
            },
            onSuccess: (data, variables) => {
                dispatch(
                    addMessage({ id: uuidv1(), text: `Saved`, priority: `low` })
                );
                queryClient.invalidateQueries(`userPrefs`);
            },
        }
    );
};

const useUpdateUserSubscriptions = () => {
    const queryClient = useQueryClient();
    const dispatch = useDispatch();
    return useMutation(
        (values) => accountClient.updateUserSubscriptions(values),
        {
            onError: (err) => {
                dispatch(
                    addMessage({
                        id: uuidv1(),
                        text: `Error updating user subscriptions: ${err}`,
                        priority: `low`,
                    })
                );
            },
            onSuccess: (data, variables) => {
                dispatch(
                    addMessage({ id: uuidv1(), text: `Saved`, priority: `low` })
                );
                queryClient.invalidateQueries(`userSubs`);
            },
        }
    );
};

export {
    useUpdatePassword,
    useUpdateUser,
    useUserPreferences,
    useUserSubscriptions,
    useUpdateUserPreferences,
    useUpdateUserSubscriptions,
};
